import { useState, useRef, useEffect } from "react";
import { needsPaymentSetupFromResponse } from "@shared/subscription-gate";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseFieldErrors, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Camera, Loader2 } from "lucide-react";
import logoImg from "@assets/Padpulse_logo_horizontal_nobg.png";
import { usePageTitle } from "@/hooks/use-page-title";

const COUNTRY_CODES = [
  { code: "+1", label: "US +1" },
  { code: "+44", label: "UK +44" },
  { code: "+91", label: "IN +91" },
  { code: "+61", label: "AU +61" },
  { code: "+49", label: "DE +49" },
  { code: "+33", label: "FR +33" },
  { code: "+81", label: "JP +81" },
  { code: "+86", label: "CN +86" },
  { code: "+55", label: "BR +55" },
  { code: "+52", label: "MX +52" },
];

interface MeUser {
  id: number;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  phone?: string | null;
  profilePictureUrl?: string | null;
  smsConsent?: boolean;
  onboardingCompletedAt?: string | null;
  onboardingWizardCompletedAt?: string | null;
  status?: string;
  approved?: boolean;
  hasPaymentMethod?: boolean;
  needsPaymentSetup?: boolean;
  tosAcknowledgedAt?: string | null;
}

interface MeResponse {
  user: MeUser;
  activeMembership?: unknown;
  isBillableAccountOwner?: boolean;
}

export default function AppOnboardingProfile() {
  const queryClient = useQueryClient();
  usePageTitle("Set Up Your Profile");
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const meQuery = useQuery<MeResponse>({ queryKey: ["/api/app/auth/me"] });

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [smsConsent, setSmsConsent] = useState(false);
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  // Track the timestamp of the most recently applied server data so we re-hydrate
  // whenever fresher data arrives (covers the back-button case too).
  const [lastHydratedAt, setLastHydratedAt] = useState<number>(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Re-hydrate the form whenever fresh user data arrives.
  // Using dataUpdatedAt as the trigger means this runs on every new fetch result,
  // whether from a remount (back-button) or an invalidated refetch.
  useEffect(() => {
    if (!meQuery.data || meQuery.dataUpdatedAt === lastHydratedAt) return;
    const u = meQuery.data.user;
    if (!u) {
      navigate("/app/login");
      return;
    }
    const inferredFirst = u.firstName || (u.fullName && !u.fullName.includes("@") ? u.fullName.split(/\s+/)[0] : "");
    const inferredLast = u.lastName || (u.fullName && !u.fullName.includes("@") ? u.fullName.split(/\s+/).slice(1).join(" ") : "");
    setFirstName(inferredFirst || "");
    setLastName(inferredLast || "");
    setProfilePicture(u.profilePictureUrl || null);
    if (u.phone) {
      const m = u.phone.match(/^(\+\d{1,3})(.*)$/);
      if (m) {
        setCountryCode(m[1]);
        setPhone(m[2]);
      } else {
        setPhone(u.phone);
      }
    } else {
      setPhone("");
    }
    setSmsConsent(!!u.smsConsent);
    setLastHydratedAt(meQuery.dataUpdatedAt);
  }, [meQuery.data, meQuery.dataUpdatedAt, lastHydratedAt, navigate]);

  useEffect(() => {
    // Task #1630 — Gate on the wizard-finished flag, not the bot-owned
    // `onboardingCompletedAt`.
    if (meQuery.data?.user?.onboardingWizardCompletedAt) {
      navigate("/app/dashboard");
    }
  }, [meQuery.data, navigate]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      const cleaned = phone.replace(/\D/g, "");
      const body = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: `${countryCode}${cleaned}`,
        smsConsent: true,
        profilePictureUrl: profilePicture || undefined,
      };
      const res = await apiRequest("POST", "/api/app/onboarding/profile", body);
      return res.json() as Promise<{ success: boolean; user: MeUser }>;
    },
    onSuccess: (data) => {
      setFieldErrors({});
      // Prime the cache with the fresh user returned by the mutation so that
      // the welcome page sees a phone-populated user immediately and does not
      // trigger its "missing phone → bounce back" guard before the refetch settles.
      if (data?.user) {
        queryClient.setQueryData<MeResponse>(["/api/app/auth/me"], (old) => ({
          user: { ...(old?.user ?? ({} as MeUser)), ...data.user },
        }));
      }
      // Background refetch for profile data; don't wait before navigating.
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile"] });
      // Approved/active users must add a card before reaching the welcome page.
      // Merge the profile response over the existing me-cache user so that
      // gating fields (tosAcknowledgedAt, hasPaymentMethod, approved) from
      // the /api/app/auth/me response are not lost — the onboarding-profile
      // endpoint only returns basic profile fields, not the card-on-file state.
      const mergedUser = data?.user
        ? { ...meQuery.data?.user, ...data.user }
        : meQuery.data?.user;
      const isApproved = mergedUser?.status === "active" || mergedUser?.approved === true;
      // Only the billable account owner is asked for a card-on-file. Team
      // members and resident members must skip this step (Task #1378).
      const isBillableAccountOwner =
        meQuery.data?.isBillableAccountOwner ?? !meQuery.data?.activeMembership;
      if (
        isApproved &&
        // Modular owners need a live subscription (Checkout), not just a card.
        needsPaymentSetupFromResponse(mergedUser) &&
        isBillableAccountOwner
      ) {
        navigate("/app/onboarding/payment");
      } else {
        navigate("/app/welcome");
      }
    },
    onError: (err: Error) => {
      const next = parseFieldErrors(err);
      if (Object.keys(next).length > 0) {
        setFieldErrors(next);
        toast({
          title: "Please fix the highlighted fields",
          description: Object.values(next).join(" "),
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Could not save",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "File too large", description: "Please upload an image under 2MB.", variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setProfilePicture(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    if (!firstName.trim() || !lastName.trim()) {
      toast({ title: "Name is required", description: "Please enter both first and last name.", variant: "destructive" });
      return;
    }
    if (phone.replace(/\D/g, "").length < 7) {
      toast({ title: "Mobile phone is required", description: "Please enter a valid mobile number.", variant: "destructive" });
      return;
    }
    if (!smsConsent) {
      toast({ title: "SMS consent required", description: "Please agree to receive SMS messages to continue.", variant: "destructive" });
      return;
    }
    submitMutation.mutate();
  };

  const initials = [firstName?.[0], lastName?.[0]].filter(Boolean).join("").toUpperCase() || (meQuery.data?.user?.email?.[0]?.toUpperCase() ?? "?");

  if (meQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-64 h-64 bg-blue-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-orange-100/30 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-lg">
        <div className="mb-6 flex justify-center">
          <img src={logoImg} alt="PadPulse" className="h-[50px] w-auto" data-testid="img-onboarding-logo" />
        </div>

        <Card className="w-full p-8 shadow-xl border border-gray-100 bg-white/90 backdrop-blur-sm">
          <h1 className="text-2xl font-bold text-gray-900 mb-1 text-center" data-testid="text-onboarding-title">
            Tell us about you
          </h1>
          <p className="text-gray-500 text-sm mb-6 text-center" data-testid="text-onboarding-subtitle">
            A few quick details before we get started.
          </p>

          <div className="flex justify-center mb-6">
            <div className="relative inline-block">
              {profilePicture ? (
                <img src={profilePicture} alt="Profile" className="h-20 w-20 rounded-full object-cover" data-testid="img-onboarding-picture" />
              ) : (
                <div className="h-20 w-20 rounded-full bg-brand flex items-center justify-center text-white text-2xl font-medium" data-testid="img-onboarding-picture">
                  {initials}
                </div>
              )}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 h-7 w-7 rounded-full bg-white border border-gray-300 flex items-center justify-center shadow-sm hover:bg-gray-50"
                data-testid="button-onboarding-edit-picture"
              >
                <Camera className="h-3.5 w-3.5 text-gray-600" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                data-testid="input-onboarding-picture"
              />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">First name *</label>
                <Input
                  value={firstName}
                  onChange={(e) => { setFirstName(e.target.value); clearFieldError("firstName"); }}
                  placeholder="First name"
                  className={fieldErrors.firstName ? "border-red-500 focus-visible:ring-red-500" : ""}
                  data-testid="input-onboarding-first-name"
                  autoFocus
                />
                {fieldErrors.firstName && (
                  <p className="text-xs text-red-600 mt-1" data-testid="error-onboarding-first-name">{fieldErrors.firstName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Last name *</label>
                <Input
                  value={lastName}
                  onChange={(e) => { setLastName(e.target.value); clearFieldError("lastName"); }}
                  placeholder="Last name"
                  className={fieldErrors.lastName ? "border-red-500 focus-visible:ring-red-500" : ""}
                  data-testid="input-onboarding-last-name"
                />
                {fieldErrors.lastName && (
                  <p className="text-xs text-red-600 mt-1" data-testid="error-onboarding-last-name">{fieldErrors.lastName}</p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Mobile phone *</label>
              <div className="flex gap-2">
                <select
                  value={countryCode}
                  onChange={(e) => { setCountryCode(e.target.value); clearFieldError("phone"); }}
                  className="h-10 rounded-md border border-gray-200 px-3 text-sm bg-white"
                  data-testid="select-onboarding-country-code"
                >
                  {COUNTRY_CODES.map((cc) => (
                    <option key={cc.code} value={cc.code}>{cc.label}</option>
                  ))}
                </select>
                <Input
                  type="tel"
                  inputMode="tel"
                  placeholder="555 123 4567"
                  value={phone}
                  onChange={(e) => { setPhone(e.target.value); clearFieldError("phone"); }}
                  className={`flex-1 ${fieldErrors.phone ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                  data-testid="input-onboarding-phone"
                />
              </div>
              {fieldErrors.phone && (
                <p className="text-xs text-red-600 mt-1" data-testid="error-onboarding-phone">{fieldErrors.phone}</p>
              )}
            </div>
            <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer select-none">
              <Checkbox
                checked={smsConsent}
                onCheckedChange={(v) => setSmsConsent(v === true)}
                className="mt-0.5"
                data-testid="checkbox-onboarding-sms-consent"
              />
              <span>
                I agree to receive transactional and marketing SMS messages from PadPulse at the number above. Message and data rates may apply. Reply STOP to opt out. See our{" "}
                <a href="/terms#sms" target="_blank" className="text-brand underline">SMS Terms</a> and{" "}
                <a href="/privacy" target="_blank" className="text-brand underline">Privacy Policy</a>.
              </span>
            </label>
            <Button
              type="submit"
              className="w-full bg-brand hover:bg-brand-hover text-white font-semibold"
              disabled={submitMutation.isPending}
              data-testid="button-onboarding-continue"
            >
              {submitMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
